import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SLOT_PLANS, WELCOME_PROMO_CODE, PERSONAL_BROKER_PROMO_CODE } from "constants/clientSlotPlans";
import { apiUrl } from "utils/apiUrl";
import { paymentApiUrl } from "utils/paymentApiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { loadPaymentConfig } from "utils/paymentConfig";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

function normalizePromo(raw) {
  return String(raw || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();
}

function clampDiscountPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.min(90, Math.max(0, Math.round(x)));
}

function computeDiscountedUsd(regularUsd, discountPercent) {
  const r = Number(regularUsd);
  if (!Number.isFinite(r) || r <= 0) return 1;
  const pct = clampDiscountPercent(discountPercent);
  const paid = Math.round((r * (100 - pct)) / 100);
  return Math.max(1, paid);
}

function savingsUsd(plan, discountPercent) {
  const r = Number(plan?.priceUsdRegular);
  if (!Number.isFinite(r)) return 0;
  const paid = computeDiscountedUsd(r, discountPercent);
  return Math.max(0, Math.round(r - paid));
}

function priceAccentClass(kind) {
  if (kind === "path2") return "text-sky-300";
  if (kind === "path1") return "text-emerald-300";
  return "text-violet-300";
}

function checkoutSubscriptionPath(applied, searchParams) {
  const fromUrl = String(searchParams?.get("path") || "")
    .trim()
    .toLowerCase();
  if (fromUrl === "path1" || fromUrl === "path2") return fromUrl;
  if (applied?.subscriptionPath === "path1" || applied?.subscriptionPath === "path2") {
    return applied.subscriptionPath;
  }
  if (applied?.kind === "path1") return "path1";
  if (applied?.kind === "path2") return "path2";
  return undefined;
}

export default function BuySlot() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!searchParams.get("from")) return;
    navigate("/BuySlotTest", { replace: true });
  }, [searchParams, navigate]);

  const [promoDraft, setPromoDraft] = useState(
    () => searchParams.get("promo") || "",
  );
  const [applied, setApplied] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [payError, setPayError] = useState(null);
  const [paymentCfg, setPaymentCfg] = useState(null);

  useEffect(() => {
    void loadPaymentConfig().then(setPaymentCfg);
  }, []);

  const validatePromo = useCallback(async (rawCode) => {
    const trimmed = String(rawCode ?? "").trim();
    const pathForCheckout = checkoutSubscriptionPath(applied, searchParams);
    if (!trimmed) {
      setPromoError(null);
      setPayError(null);
      setApplied(null);
      return false;
    }

    setPromoError(null);
    setPayError(null);
    setApplyingPromo(true);
    try {
      const validateBody = { code: trimmed };
      if (pathForCheckout) validateBody.subscriptionPath = pathForCheckout;
      const res = await fetch(apiUrl("/api/client/validate-slot-promo"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validateBody),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setApplied(null);
        setPromoError(
          clientSafeMessage(
            data.message,
            res.status === 401
              ? "Sign in to validate a promo code."
              : "This promo code is not valid.",
          ),
        );
        return false;
      }
      setApplied({
        code: data.code,
        discountPercent: data.discountPercent,
        kind: data.kind,
        subscriptionPath: data.subscriptionPath || null,
        pathLabel: data.pathLabel,
        source: data.source,
        infoMessage: data.message || "Promo code applied.",
      });
      setPromoDraft(String(data.code ?? trimmed ?? ""));
      return true;
    } catch (e) {
      setApplied(null);
      setPromoError(clientSafeMessage(e?.message, "Network error."));
      return false;
    } finally {
      setApplyingPromo(false);
    }
  }, [applied, searchParams]);

  useEffect(() => {
    const fromUrl = String(searchParams.get("promo") || "").trim();
    if (!fromUrl) return;
    setPromoDraft(fromUrl);
    void validatePromo(fromUrl);
  }, [searchParams, validatePromo]);

  const pathTitle = useMemo(() => {
    if (!applied) return "Choose your plan";
    if (applied.kind === "path2") return "Path 2 — Personal broker";
    if (applied.kind === "path1") return "Path 1 — Broker partner";
    return `Promo — ${applied.discountPercent}%`;
  }, [applied]);

  async function startPayment(planId) {
    setPayError(null);
    setLoadingPlanId(planId);
    const pathForCheckout = checkoutSubscriptionPath(applied, searchParams);
    try {
      const payBody = {
        planId,
        promo: applied?.code ?? "",
      };
      if (pathForCheckout) payBody.subscriptionPath = pathForCheckout;
      const cfg = paymentCfg || (await loadPaymentConfig());
      const payPath = cfg?.slotPaymentEndpoint || "/api/client/slot-payment";
      const res = await fetch(paymentApiUrl(payPath), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payBody),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setPayError(
          clientSafeMessage(
            data.message,
            res.status === 401
              ? "Please sign in to continue."
              : "Payment could not be started. Please try again.",
          ),
        );
        return;
      }
      navigate("/PayUSDT", {
        replace: false,
        state: {
          planId: data.planId || planId,
          promo: data.promo || applied?.code || null,
          discountPercent: data.discountPercent ?? applied?.discountPercent ?? 0,
          kind: applied?.kind || null,
          pathLabel: applied?.pathLabel || null,
          appliedMessage: applied?.infoMessage || null,
          regularPriceUsd: data.regularPriceUsd,
          priceUsd: data.priceUsd,
          bep20: data.bep20,
        },
      });
    } catch (e) {
      setPayError(clientSafeMessage(e?.message, "Network error."));
    } finally {
      setLoadingPlanId(null);
    }
  }

  const backTo = "/client/subscription";

  const accent = applied ? priceAccentClass(applied.kind) : "text-brand-300";

  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-6xl space-y-8">
        <LegalPagesBrandHeader />

        <header className="text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">Buy a slot</h1>
          <p className="mt-2 text-lg font-semibold text-gray-200">{pathTitle}</p>
          <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Pay in <strong className="text-white">USDT BEP20 (BSC)</strong> via{" "}
            <strong className="text-white">
              {paymentCfg?.providerLabel || "crypto gateway"}
            </strong>
            . Prices
            shown after you click <strong className="text-white">Apply</strong> use your promo code.
            Public codes — Path 1:{" "}
            <code className="rounded bg-white/10 px-1 font-mono text-brand-200">{WELCOME_PROMO_CODE}</code>
            {" · "}
            Path 2:{" "}
            <code className="rounded bg-white/10 px-1 font-mono text-brand-200">
              {PERSONAL_BROKER_PROMO_CODE}
            </code>
            . Other custom codes may exist in our system.
          </p>
        </header>

        <section className="mx-auto max-w-md rounded-2xl border border-white/10 bg-white/[0.06] p-5 shadow-xl">
          <label
            htmlFor="buy-slot-promo"
            className="block text-xs font-semibold uppercase tracking-wide text-gray-400"
          >
            Promo code
          </label>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <input
              id="buy-slot-promo"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={promoDraft}
              onChange={(e) => {
                const v = e.target.value;
                setPromoDraft(v);
                if (!String(v).trim()) {
                  setPromoError(null);
                }
                if (applied && normalizePromo(v) !== normalizePromo(applied.code)) {
                  setApplied(null);
                  setPromoError(null);
                }
              }}
              placeholder="Enter your promo code"
              className="w-full flex-1 rounded-xl border border-white/15 bg-[#0a102c] px-4 py-3 font-mono text-sm text-white outline-none ring-brand-400/30 placeholder:text-gray-500 focus:border-brand-400 focus:ring-2"
            />
            <button
              type="button"
              disabled={applyingPromo}
              onClick={() => void validatePromo(promoDraft)}
              className="shrink-0 rounded-xl border border-brand-400/60 bg-brand-500/90 px-5 py-3 text-sm font-bold text-white transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-45 sm:min-w-[9.5rem]"
            >
              {applyingPromo ? "Checking…" : "Apply"}
            </button>
          </div>
          {applyingPromo ? (
            <p className="mt-2 text-xs text-gray-400">Checking promo code…</p>
          ) : promoError ? (
            <p className="mt-2 text-xs font-medium text-red-300" role="alert">
              {promoError}
            </p>
          ) : applied ? (
            <p className="mt-2 text-xs font-medium text-emerald-300/95">{applied.infoMessage}</p>
          ) : (
            <p className="mt-2 text-xs text-gray-400">
              Enter a code and click <span className="text-gray-200">Apply</span> to update prices,
              or pay at the regular price without a code.
            </p>
          )}
        </section>

        {payError ? (
          <div
            className="rounded-xl border border-red-400/40 bg-red-950/50 px-4 py-3 text-center text-sm text-red-100"
            role="alert"
          >
            {payError}
            {String(payError).toLowerCase().includes("sign in") ? (
              <p className="mt-2">
                <Link
                  to="/SignIn"
                  className="font-semibold text-brand-300 underline-offset-2 hover:underline"
                >
                  Go to Sign in
                </Link>
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          {SLOT_PLANS.map((plan) => {
            const hasApplied = Boolean(applied?.code);
            const pct = applied?.discountPercent ?? 0;
            const paidUsd = hasApplied ? computeDiscountedUsd(plan.priceUsdRegular, pct) : null;
            const save = hasApplied ? savingsUsd(plan, pct) : 0;
            return (
              <section
                key={plan.id}
                className={`flex flex-col rounded-2xl border border-white/10 bg-white/[0.06] p-6 shadow-xl ${plan.accent}`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{plan.period}</p>
                <h2 className="mt-1 text-xl font-bold text-white">{plan.name}</h2>
                <p className="mt-1 text-xs text-gray-400">{plan.cadence}</p>
                <p className="mt-3 text-sm text-gray-300">{plan.slots}</p>
                <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  {hasApplied ? (
                    <>
                      <span className="text-sm text-gray-500 line-through">
                        {plan.regularPriceLabel}
                        {plan.priceSuffix}
                      </span>
                      <span className={`text-2xl font-bold tabular-nums ${accent}`}>
                        {formatUsd(paidUsd)}
                        <span className="text-sm font-medium text-gray-400">{plan.priceSuffix}</span>
                      </span>
                      <span className="rounded-md border border-amber-400/50 bg-amber-500/15 px-2 py-0.5 text-xs font-bold text-amber-100">
                        Save ${save} ({pct}%)
                      </span>
                    </>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="text-2xl font-bold tabular-nums text-gray-100">
                          {plan.regularPriceLabel}
                          <span className="text-sm font-medium text-gray-400">{plan.priceSuffix}</span>
                        </span>
                        <span className="rounded-md border border-white/15 bg-white/5 px-2 py-0.5 text-xs font-medium text-gray-400">
                          No discount
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Regular price — use a code and click <strong className="text-gray-400">Apply</strong>{" "}
                        to see a discounted price if available.
                      </p>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  disabled={loadingPlanId != null}
                  onClick={() => startPayment(plan.id)}
                  className="mt-6 w-full rounded-xl bg-brand-500 py-3 text-sm font-bold text-white transition hover:bg-brand-600 disabled:opacity-50"
                >
                  {loadingPlanId === plan.id ? "Creating payment…" : "Pay with USDT (BEP20)"}
                </button>
              </section>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-400">
          <Link to={backTo} className="text-brand-300 hover:underline">
            Back to subscription
          </Link>
        </p>

        <footer className="border-t border-white/10 pt-6 text-center text-xs text-gray-400">
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}

function formatUsd(n) {
  return `$${Math.round(Number(n))}`;
}
